import { ITemplate } from '../../interface/ITemplate'
import { ITimecard, TimeCardLine } from '../../interface/ISelectedDate'
import { IJournal, IJournalLine } from '../../interface/IJournal'
import { IUser } from '../../interface/IUser'
import { IProject, IProjectAndActivity } from '../../interface/IProjects'
import axios from 'axios'
import { parseTemplateDateToIso } from './timeUtils'
import { injectedEndpoints } from '@/api/InjectedEndpoints'
import { store } from '../../source/Store'
import { EmptyGuid } from '../values'

const SAVE_TEMPLATE_URL = `${process.env.REACT_APP_BACKEND_URL}/SaveTemplate`
const DELETE_TEMPLATE_URL = `${process.env.REACT_APP_BACKEND_URL}/Template/{id}`
const SET_DEFAULT_TEMPLATE_URL = `${process.env.REACT_APP_BACKEND_URL}/Template/{id}/SetDefault`

type TemplatePayload = ITemplate & {
  timeCardHeader?: ITemplate['TimeCardHeader']
  timeCardLines?: ITemplate['TimeCardLines']
}

export function getTemplateHeader(template?: TemplatePayload | any) {
  return (template?.timeCardHeader || template?.TimeCardHeader || null) as ITemplate['TimeCardHeader']
}

export function getTemplateLines(template?: TemplatePayload | any) {
  return (template?.timeCardLines || template?.TimeCardLines || []) as NonNullable<ITemplate['TimeCardLines']>
}

export async function fetchTemplates(force: boolean): Promise<any[]> {
  try {
    const data = await store
      .dispatch(
        injectedEndpoints.endpoints.getTemplates.initiate(undefined, {
          forceRefetch: force,
          subscribe: false,
        })
      )
      .unwrap()

    return Array.isArray(data) ? data : []
  } catch (error) {
    console.log(error)
    return []
  }
}

export function buildTemplate(selectedDate?: ITimecard, templateName?: string): ITemplate | null {
  if (!selectedDate) return null
  const lines = selectedDate.lines || []

  const mapLine = (l: TimeCardLine) => ({
    workerpersonnelnumber: l.mserp_workerpersonnelnumber || undefined,
    timeandattendancejobid: l.mserp_timeandattendancejobid || '',
    indirectactivitycategoryid: l.mserp_indirectactivitycategoryid || '',
    projectid: l.mserp_projectid || '',
    startdatetime: l.mserp_startdatetime || undefined,
    stopdatetime: l.mserp_stopdatetime || l.mserp_startdatetime || undefined,
    calculatedtimeseconds: l.mserp_calculatedtimeseconds || 0,
    purchid: l.mserp_purchid || '',
    purchlinenumber: l.mserp_purchlinenumber || 0,
    oncalltimeandattendancejobid: l.mserp_oncalltimeandattendancejobid || '',
    mboncallapplicationid: l.mserp_mboncallapplicationid || '',
  })

  const activeLines = lines.filter((l) => !l.delete)

  const tcLines = activeLines.map(mapLine)

  const header = {
    workerpersonnelnumber: lines[0]?.mserp_workerpersonnelnumber || undefined,
    templatename: templateName || undefined,
    traveltime: selectedDate?.header?.mserp_mbquantitytravel || 0,
    default: false,
  }

  return {
    TimeCardHeader: header as any,
    TimeCardLines: tcLines as any,
  } as ITemplate
}

const findActivityForJournalLine = (line: IJournalLine, projects: IProject[] = []): IProjectAndActivity | undefined => {
  const project = projects.find((item) => item.mserp_projectid === line.mserp_projectid)
  return project?.activities?.find((activity) => activity.mserp_activitynumber === line.mserp_activitynumber)
}

export function buildJournalTemplate(selectedJournal?: IJournal, templateName?: string, projects: IProject[] = [], user?: IUser): ITemplate | null {
  if (!selectedJournal) return null

  const activeLines = (selectedJournal.lines || []).filter((line) => !line.delete)
  if (activeLines.length === 0) return null

  const journalTemplateLines = activeLines.map((line: IJournalLine) => {
    const activity = findActivityForJournalLine(line, projects)

    return {
      workerpersonnelnumber: user?.worker?.mserp_personnelnumber || undefined,
      timeandattendancejobid: activity?.mserp_jobid || line.mserp_activitynumber || '',
      indirectactivitycategoryid: line.mserp_activitynumber || '',
      projectid: line.mserp_projectid || '',
      calculatedtimeseconds: Math.round((line.mserp_hours || 0) * 60 * 60),
      purchlinenumber: line.mserp_mbpurchlinerefrecid || 0,
      mboncallapplicationid: line.mserp_category || '',
    }
  })

  return {
    TimeCardHeader: {
      workerpersonnelnumber: user?.worker?.mserp_personnelnumber || undefined,
      templatename: templateName || undefined,
      traveltime: 0,
      default: false,
    },
    TimeCardLines: journalTemplateLines as NonNullable<ITemplate['TimeCardLines']>,
  }
}

export async function postTemplate(template: ITemplate, config?: any): Promise<any> {
  const response = await axios.post<any>(SAVE_TEMPLATE_URL, template, config)
  return response.data
}

export async function deleteTemplate(id: string, config?: any): Promise<any> {
  const response = await axios.delete<any>(DELETE_TEMPLATE_URL.replace('{id}', id), config)
  return response.data
}

export function applyTemplateToTimecard(selectedDate?: ITimecard, tpl?: ITemplate | any): ITimecard | null {
  if (!selectedDate || !tpl) return null
  const clone: any = JSON.parse(JSON.stringify(selectedDate))
  clone.lines = []
  const dateIso = selectedDate.header?.mserp_timeprofiledate || new Date().toISOString()
  const baseDate = new Date(dateIso)

  const lines = getTemplateLines(tpl)
  for (const tLine of lines) {
    const jobId = tLine.timeandattendancejobid || ''
    if (!jobId) continue
    const line = {
      mserp_timeprofiledate: selectedDate.header?.mserp_timeprofiledate,
      mserp_timeandattendancejobid: jobId,
      mserp_calculatedtimeseconds: 0,
      delete: false,
      mserp_indirectactivitycategoryid: (tLine.indirectactivitycategoryid || '').toUpperCase(),
    } as TimeCardLine
    if (tLine.projectid) line.mserp_projectid = tLine.projectid

    if (tLine.oncalltimeandattendancejobid) {
      line.mserp_oncalltimeandattendancejobid = tLine.oncalltimeandattendancejobid
    }
    if (tLine.mboncallapplicationid !== undefined) {
      line.mserp_mboncallapplicationid = tLine.mboncallapplicationid
    }

    if (tLine.purchid) line.mserp_purchid = tLine.purchid
    if (tLine.purchlinenumber) line.mserp_purchlinenumber = tLine.purchlinenumber

    const startTimeRaw = tLine.startdatetime || undefined
    const stopTimeRaw = tLine.stopdatetime || undefined
    const startIso = parseTemplateDateToIso(startTimeRaw, baseDate, dateIso)
    const stopIso = parseTemplateDateToIso(stopTimeRaw, baseDate, dateIso)
    if (startIso) {
      line.mserp_startdatetime = startIso
      line.mserp_stopdatetime = startIso
    }
    if (stopIso) {
      line.mserp_startdatetime = line.mserp_startdatetime || stopIso
      line.mserp_stopdatetime = stopIso
    }

    if (typeof tLine.calculatedtimeseconds === 'number') {
      line.mserp_calculatedtimeseconds = tLine.calculatedtimeseconds
    }
    clone.lines.push(line)
  }

  const travelTime = getTemplateHeader(tpl)?.traveltime || 0
  clone.header = clone.header || {}
  clone.header.mserp_mbquantitytravel = travelTime
  return clone as ITimecard
}

export function applyTemplateToJournal(selectedJournal?: IJournal, tpl?: ITemplate | any, user?: IUser): IJournal | null {
  if (!selectedJournal || !tpl || !user?.resource) return null

  const clone: IJournal = JSON.parse(JSON.stringify(selectedJournal))
  clone.lines = []

  const lines = getTemplateLines(tpl)
  for (const templateLine of lines) {
    clone.lines.push({
      mserp_dataareaid: user.resource.mserp_resourcecompanyid,
      mserp_activitynumber: templateLine.indirectactivitycategoryid || templateLine.timeandattendancejobid || '',
      mserp_category: templateLine.mboncallapplicationid || '',
      mserp_linenumber: 0,
      mserp_journalid: clone.header?.mserp_journalbatchnumber,
      mserp_projectdate: clone.header?.date,
      mserp_hours: Math.round((((templateLine.calculatedtimeseconds || 0) / 3600) + Number.EPSILON) * 100) / 100,
      mserp_mbpurchlinerefrecid: templateLine.purchlinenumber || 0,
      mserp_projectid: templateLine.projectid || '',
      mserp_projjournaltransentityid: EmptyGuid,
      mserp_resourceid: user.resource.mserp_resourceid,
      date: clone.header?.date,
      delete: false,
    })
  }

  return clone
}

export async function setDefaultTemplate(templateId: string, config?: any): Promise<void> {
  if (!templateId) return
  const response = await axios.put<any>(SET_DEFAULT_TEMPLATE_URL.replace('{id}', templateId), {}, config)
  return response.data
}
